import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { apiService } from '../../services/api';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';
import { ListedKudos } from '../home/types';
import { emojiOf } from '../home/kudos';
import * as fmt from '../../lib/format';

const PAGE = 20;

// Settings → Kudos: every kudos that leaders and admins sent, newest first, so an admin can find one and take it
// back (a wrong recipient, a note that should not stay). A page at a time with "Show more": there is no total and no
// count per person or team on purpose, so this can never read as a scoreboard.
export default function KudosTab() {
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [rows, setRows] = useState<ListedKudos[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [search, setSearch] = useState('');
  // A newer request makes an older answer irrelevant: only the latest one is kept.
  const latest = useRef(0);

  // What was typed becomes the search a moment after typing stops.
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(typed.trim()), 300);
    return () => window.clearTimeout(t);
  }, [typed]);

  const load = useCallback(
    async (after: string | null) => {
      const id = ++latest.current;
      after ? setLoadingMore(true) : setLoading(true);
      setError(null);
      try {
        const r = await apiService.getKudosAll({ limit: PAGE, ...(after ? { after } : {}), ...(search ? { q: search } : {}) });
        if (id !== latest.current) return;
        if (!r.success) throw new Error(r.error || 'Could not load the kudos');
        setRows((cur) => (after ? [...cur, ...(r.data ?? [])] : r.data ?? []));
        setNext(r.meta?.has_more ? r.meta.next ?? null : null);
      } catch (e: any) {
        if (id === latest.current) setError(e?.response?.data?.error || e?.message || 'Could not load the kudos');
      } finally {
        if (id === latest.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [search]
  );

  useEffect(() => {
    load(null);
  }, [load]);

  const remove = async (k: ListedKudos) => {
    const yes = await confirm({
      title: `Remove ${k.from_name}’s kudos to ${k.to_name}?`,
      message: 'They will no longer see it in their Updates. The person who gave it is not told.',
      confirmLabel: 'Remove kudos',
    });
    if (!yes) return;
    try {
      const r = await apiService.deleteKudos(k.id);
      if (!r.success) throw new Error(r.error || 'Could not remove the kudos');
      // Taken out of the list where it is, so the person keeps their place in it.
      setRows((cur) => cur.filter((x) => x.id !== k.id));
      showToast('success', 'Removed the kudos');
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not remove the kudos');
    }
  };

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-gray-700">
        Every kudos that leaders and admins have sent, newest first. Remove one if it went to the wrong person or the note should not stay. Nothing here is counted or ranked.
      </p>

      <div className="relative max-w-sm">
        <label htmlFor="kudos-search" className="sr-only">
          Search kudos
        </label>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden />
        <input
          id="kudos-search"
          type="search"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="Search by giver, recipient, project or note"
          autoComplete="off"
          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-500 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
      </div>

      {error && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200">
          <span>{error}</span>
          <button type="button" onClick={() => load(null)} className="rounded-lg bg-white px-3 py-1 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 hover:bg-rose-100">
            Try again
          </button>
        </div>
      )}

      <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
        {loading && rows.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-600" role="status">
            Loading the kudos…
          </p>
        ) : rows.length === 0 && !error ? (
          <p className="px-5 py-8 text-center text-sm text-gray-600">{search ? 'No kudos match that.' : 'No kudos have been given yet.'}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {rows.map((k) => (
              <li key={k.id} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-5 py-3.5">
                <div className="min-w-0 grow basis-64">
                  <p className="break-words text-sm text-gray-900">
                    <span role="img" aria-label={emojiOf(k.emoji)?.label ?? 'Kudos'} className="mr-1.5">
                      {emojiOf(k.emoji)?.glyph ?? '🙂'}
                    </span>
                    <span className="font-medium">{k.from_name}</span> gave kudos to <span className="font-medium">{k.to_name}</span>
                    {k.project_key ? ` for ${k.project_name ? `${k.project_name} (${k.project_key})` : k.project_key}` : ''}
                  </p>
                  {k.note && <p className="mt-0.5 break-words text-sm text-gray-700">“{k.note}”</p>}
                  <p className="mt-0.5 text-xs text-gray-600">{fmt.dateTime(k.created_at)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => remove(k)}
                  aria-label={`Remove ${k.from_name}’s kudos to ${k.to_name}`}
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-rose-700 ring-1 ring-inset ring-rose-200 hover:bg-rose-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {next && (
        <button
          type="button"
          onClick={() => load(next)}
          disabled={loadingMore}
          className="rounded-lg px-4 py-2 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-200 hover:bg-blue-50 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          {loadingMore ? 'Loading…' : 'Show more'}
        </button>
      )}
    </div>
  );
}
